import React, { useCallback, useRef, useState } from "react";
import { FlightPackageContentFragment } from "types/graphql-types";
import * as styles from "./CruiseItineraryFlightPackage.module.scss";
import { Icon } from "@silversea-ssc/ui-components-react";
import {
  ItineraryItemExpand,
  ItineraryItemTitle
} from "../CruiseItineraryItem";
import CruiseItineraryItemDetails from "../CruiseItineraryItemDetails";
import { IconsId } from "@silversea-ssc/design-system-icons";
import IncludedWithOptionalFlightPackage from "./IncludedWithOptionalFlightPackage";
import { match } from "ts-pattern";
import { t } from "@lingui/core/macro";

type CruiseItineraryFlightPackageProps = {
  flightPackage: FlightPackageContentFragment;
};

const CruiseItineraryFlightPackage: React.FC<
  CruiseItineraryFlightPackageProps
> = ({ flightPackage }) => {
  const [opened, setOpened] = useState(false);

  const detailsRef = useRef<HTMLDivElement | null>(null);
  const toggleDetails = useCallback(
    (
      e:
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.KeyboardEvent<HTMLDivElement>
    ) => {
      e.preventDefault();
      if (
        !detailsRef?.current ||
        !detailsRef.current.contains(e.target as Node)
      ) {
        setOpened(!opened);
      }
    },
    [opened]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.code == "Space" || e.code == "Enter") toggleDetails(e);
    },
    [toggleDetails]
  );

  return (
    <div
      className={styles.item}
      onClick={toggleDetails}
      onKeyDown={onKeyDown}
      role="button"
      aria-pressed={opened}
      tabIndex={0}
      style={{ cursor: "pointer" }}
      data-content-piece="details_button"
    >
      <div className={styles.faresBadge}>
        <IncludedWithOptionalFlightPackage />
      </div>
      <div className={styles.header}>
        <div className={styles.icon}>
          <Icon
            name={flightPackage.icon as IconsId}
            padding={false}
            size="2.5x"
            ariaHidden={true}
          />
        </div>
        <ItineraryItemTitle title={flightPackage.title?.localized} />
      </div>

      <div className={styles.inclusions}>
        {flightPackage.inclusions?.map((inc, idx) =>
          match(inc)
            .with("Transfer", () => (
              <InclusionItem
                key={`${inc}-${idx}`}
                className={styles.inclusionItem}
                iconName="free-transfers"
                label={t`Airport transfers always included`}
              />
            ))
            .with("Hotel", () => (
              <InclusionItem
                key={`${inc}-${idx}`}
                className={styles.inclusionItem}
                iconName="free-hotel"
                label={t`Hotel included when needed`}
              />
            ))
            .otherwise(() => null)
        )}
      </div>
      <ItineraryItemExpand opened={opened} className={styles.expand} />
      {opened && (
        <div className={styles.details} ref={detailsRef}>
          <React.Suspense fallback={<div></div>}>
            <CruiseItineraryItemDetails
              title={flightPackage.title?.localized}
              description={flightPackage.content?.localized}
            />
          </React.Suspense>
        </div>
      )}
    </div>
  );
};

export default CruiseItineraryFlightPackage;

type InclusionItemProps = {
  iconName: IconsId;
  className: string;
  label: string;
};
const InclusionItem: React.FC<InclusionItemProps> = ({
  iconName,
  className,
  label
}) => {
  return (
    <div className={className}>
      <Icon name={iconName} size="1.5x" padding={false} />
      {label}
    </div>
  );
};
